import React, { useState, useRef, useEffect } from 'react';
import { Send, Image, Bot, Brain, Target, PenTool, Smartphone, BarChart, Palette, MoreVertical } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Message, Attachment, Assistant } from '../types/Assistant';
import { generateResponse } from '../lib/n8n';
import { assistants, getAssistantById } from '../data/assistants';
import ReactMarkdown from 'react-markdown';
import { getMessages, addMessage } from '../lib/chat';

type ChatProps = {
  assistantId: string | null;
};

export default function Chat({ assistantId }: ChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const coordinator = assistants.find(a => a.id === 'coordinator')!;

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const dbMessages = await getMessages();
      const formattedMessages: Message[] = dbMessages.map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.role,
        assistantId: msg.assistant_id || undefined,
        timestamp: new Date(msg.created_at)
      }));
      setMessages(formattedMessages);
      setError(null);
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('Erro ao carregar mensagens. Tente novamente.');
    }
  };

  const getAssistantIcon = (icon: string) => {
    const icons = {
      brain: Brain,
      target: Target,
      'pen-tool': PenTool,
      smartphone: Smartphone,
      palette: Palette,
      'bar-chart': BarChart
    };
    const IconComponent = icons[icon as keyof typeof icons] || Bot;
    return <IconComponent className="w-4 h-4" />;
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newAttachment: Attachment = {
            id: Date.now().toString(),
            type: 'image',
            url: e.target?.result as string,
            name: file.name,
            size: file.size,
            previewUrl: e.target?.result as string
          };
          setAttachments(prev => [...prev, newAttachment]);
        };
        reader.readAsDataURL(file);
      }
    });

    event.target.value = '';
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!input.trim() && attachments.length === 0) || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      role: 'user',
      timestamp: new Date(),
      attachments: attachments
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setAttachments([]);
    setIsLoading(true);
    setError(null);

    try {
      // Save user message
      await addMessage({
        role: 'user',
        content: input
      });

      // Generate AI response
      const response = await generateResponse([{ role: 'user', content: input }], coordinator.id);

      const assistantMessage: Message = {
        id: Date.now().toString(),
        content: response,
        role: 'assistant',
        assistantId: coordinator.id,
        timestamp: new Date()
      };

      // Save assistant message
      await addMessage({
        role: 'assistant',
        content: response,
        assistant_id: coordinator.id
      });

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao processar mensagem';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const TypingIndicator = () => (
    <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm max-w-[80%]">
      <div className="flex items-center gap-2">
        {getAssistantIcon(coordinator.icon)}
        <span className="font-medium">{coordinator.name}</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span className="text-sm text-gray-500">Gerando resposta...</span>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Agents List */}
      <div className="flex-none bg-white border-b">
        <div className="grid grid-cols-4 gap-1 p-2">
          {assistants.map((assistant) => (
            <button
              key={assistant.id}
              className="flex flex-col items-center p-2 rounded hover:bg-purple-50 transition-colors"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-full mb-1">
                {getAssistantIcon(assistant.icon)}
              </div>
              <div className="text-center min-w-0">
                <div className="font-medium text-sm truncate">{assistant.name}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area - Scrollable */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto bg-gray-50"
        style={{ height: 'calc(100vh - 180px)' }}
      >
        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
              {error}
            </div>
          )}

          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Bot className="w-16 h-16 mx-auto text-purple-200 mb-4" />
                <p className="text-gray-500">
                  Comece a conversar! Digite sua mensagem abaixo.
                </p>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] rounded-xl p-4 ${
                  message.role === 'user' 
                    ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white' 
                    : 'bg-white border shadow-sm'
                }`}>
                  {message.role === 'assistant' && message.assistantId && (
                    <div className="flex items-center justify-between mb-3 pb-2 border-b">
                      <div className="flex items-center gap-2">
                        {message.assistantId && getAssistantIcon(getAssistantById(message.assistantId)?.icon || 'bot')}
                        <span className="font-medium">
                          {getAssistantById(message.assistantId)?.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {format(message.timestamp, 'HH:mm', { locale: ptBR })}
                        </span>
                      </div>
                      <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  )}

                  <div className={message.role === 'assistant' ? 'prose prose-purple prose-sm max-w-none' : ''}>
                    {message.role === 'assistant' ? (
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    ) : (
                      <p className="text-white">{message.content}</p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="flex justify-start">
              <TypingIndicator />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - Fixed at bottom */}
      <div className="flex-none bg-white border-t sticky bottom-0 left-0 right-0">
        <form onSubmit={handleSend} className="p-4 flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Digite sua mensagem..."
            className="flex-1 px-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            multiple
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Image className="w-5 h-5 text-gray-600" />
          </button>
          <button
            type="submit"
            disabled={isLoading || (!input.trim() && attachments.length === 0)}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
}